import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid2 as Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Paper,
  LinearProgress,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  Visibility,
  Add,
  Send,
  PhotoCamera,
  CloudUpload,
  Delete,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const ContractorPermitsPage = () => {
  const navigate = useNavigate();

  const [permits, setPermits] = useState([]);
  const [selectedPermit, setSelectedPermit] = useState(null);
  const [inspectionDialogOpen, setInspectionDialogOpen] = useState(false);
  const [selectedInspections, setSelectedInspections] = useState([]);
  const [inspectionComments, setInspectionComments] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Mock permit data for contractor view
  const mockPermits = useMemo(
    () => [
      {
        id: "PKG2024001",
        number: "PKG2024001",
        address: "123 Oak Street, Jacksonville, FL 32207",
        type: "residential",
        status: "active",
        submittedDate: new Date(
          Date.now() - 30 * 24 * 60 * 60 * 1000
        ).toISOString(),
        approvedDate: new Date(
          Date.now() - 15 * 24 * 60 * 60 * 1000
        ).toISOString(),
        availableInspections: [
          {
            id: "I001",
            name: "Footing/Foundation",
            required: true,
            status: "passed",
            dependencies: [],
            canRequest: false, // Already passed
          },
          {
            id: "I002",
            name: "Framing",
            required: true,
            status: "not_requested",
            dependencies: ["I001"],
            canRequest: true,
          },
          {
            id: "I003",
            name: "Electrical Rough-In",
            required: true,
            status: "not_requested",
            dependencies: ["I002"],
            canRequest: false, // Depends on framing
          },
          {
            id: "I004",
            name: "Plumbing Rough-In",
            required: true,
            status: "not_requested",
            dependencies: ["I002"],
            canRequest: true,
          },
          {
            id: "I005",
            name: "HVAC Rough-In",
            required: true,
            status: "not_requested",
            dependencies: ["I002"],
            canRequest: true,
          },
          {
            id: "I006",
            name: "Insulation",
            required: false,
            status: "not_requested",
            dependencies: ["I003", "I004", "I005"],
            canRequest: false,
          },
        ],
      },
      {
        id: "PKG2024002",
        number: "PKG2024002",
        address: "456 Pine Avenue, St Augustine, FL 32084",
        type: "commercial",
        status: "in_review",
        submittedDate: new Date(
          Date.now() - 5 * 24 * 60 * 60 * 1000
        ).toISOString(),
        approvedDate: null,
        availableInspections: [
          {
            id: "C001",
            name: "Foundation",
            required: true,
            status: "not_requested",
            dependencies: [],
            canRequest: false, // Permit not active yet
          },
        ],
      },
      {
        id: "PKG2024003",
        number: "PKG2024003",
        address: "789 Maple Drive, Green Cove Springs, FL 32043",
        type: "residential",
        status: "corrections_needed",
        submittedDate: new Date(
          Date.now() - 10 * 24 * 60 * 60 * 1000
        ).toISOString(),
        approvedDate: null,
        correctionNotes: "Structural plans need revision per IBC 1604.3",
        availableInspections: [],
      },
    ],
    []
  );

  useEffect(() => {
    setPermits(mockPermits);
  }, [mockPermits]);

  const handleRequestInspections = async () => {
    if (!selectedPermit || selectedInspections.length === 0) return;

    try {
      setSubmitting(true);

      // Mock API call to request inspections
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Update permit state
      setPermits((prev) =>
        prev.map((permit) =>
          permit.id === selectedPermit.id
            ? {
                ...permit,
                availableInspections: permit.availableInspections.map(
                  (inspection) =>
                    selectedInspections.includes(inspection.id)
                      ? { ...inspection, status: "pending" }
                      : inspection
                ),
              }
            : permit
        )
      );

      // Show success message
      alert(
        `Successfully requested ${selectedInspections.length} inspection(s)!`
      );

      setInspectionDialogOpen(false);
      setSelectedInspections([]);
      setInspectionComments("");
      setUploadedFiles([]);
    } catch (error) {
      console.error("Failed to request inspections:", error);
      alert("Failed to request inspections. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      new: { color: "default", label: "New" },
      active: { color: "success", label: "Active" },
      in_review: { color: "info", label: "In Review" },
      submitted_to_city: { color: "primary", label: "Submitted to City" },
      ready_to_submit: { color: "warning", label: "Ready to Submit" },
      corrections_needed: { color: "error", label: "Corrections Needed" },
      cancelled: { color: "error", label: "Cancelled" },
      finalized: { color: "success", label: "Finalized" },
    };

    const config = statusConfig[status] || { color: "default", label: status };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const getInspectionStatusChip = (status) => {
    const statusConfig = {
      not_requested: { color: "default", label: "Not Requested" },
      pending: { color: "warning", label: "Pending" },
      approved: { color: "primary", label: "Approved" },
      passed: { color: "success", label: "Passed" },
      failed: { color: "error", label: "Failed" },
      corrections_needed: { color: "warning", label: "Needs Correction" },
    };

    const config = statusConfig[status] || { color: "default", label: status };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const canRequestInspections = (permit) => {
    return (
      permit.status === "active" &&
      permit.availableInspections.some((i) => i.canRequest)
    );
  };

  const getAvailableInspections = (permit) => {
    return permit.availableInspections.filter(
      (i) =>
        i.canRequest && (i.status === "not_requested" || i.status === "pending")
    );
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const newFiles = files.map((file, index) => ({
      id: `FILE_${Date.now()}_${index}`,
      name: file.name,
      type: file.type,
      size: `${(file.size / 1024 / 1024).toFixed(1)}MB`,
      file,
    }));

    setUploadedFiles([...uploadedFiles, ...newFiles]);
  };

  const handleRemoveFile = (fileId) => {
    setUploadedFiles(uploadedFiles.filter((f) => f.id !== fileId));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography color="primary" variant="h4" sx={{ mb: 3 }}>
        My Permits
      </Typography>

      {/* Permits Grid */}
      <Grid container spacing={3}>
        {permits.length === 0 ? (
          <Grid size={12}>
            <Alert severity="info">
              No permits found. Contact your Private Provider to get started.
            </Alert>
          </Grid>
        ) : (
          permits.map((permit) => (
            <Grid key={permit.id} size={{ xs: 12, md: 6, lg: 4 }}>
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {permit.number}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        {permit.address}
                      </Typography>
                      <Chip
                        label={permit.type}
                        color="primary"
                        size="small"
                        variant="outlined"
                        sx={{ textTransform: "capitalize", mr: 1 }}
                      />
                    </Box>
                    {getStatusChip(permit.status)}
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Submitted:{" "}
                      {new Date(permit.submittedDate).toLocaleDateString()}
                    </Typography>
                    {permit.approvedDate && (
                      <Typography
                        variant="caption"
                        color="success.main"
                        sx={{ display: "block" }}
                      >
                        Approved:{" "}
                        {new Date(permit.approvedDate).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>

                  {permit.correctionNotes && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Corrections Needed:</strong>{" "}
                        {permit.correctionNotes}
                      </Typography>
                    </Alert>
                  )}

                  {/* Inspection Summary */}
                  <Typography variant="subtitle2" gutterBottom>
                    Inspections ({permit.availableInspections?.length || 0})
                  </Typography>

                  {permit.availableInspections?.length > 0 ? (
                    <Box sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                          mb: 1,
                        }}
                      >
                        {permit.availableInspections
                          .slice(0, 3)
                          .map((inspection) => (
                            <Chip
                              key={inspection.id}
                              label={inspection.name}
                              size="small"
                              color={
                                inspection.status === "passed"
                                  ? "success"
                                  : inspection.status === "failed"
                                    ? "error"
                                    : inspection.status === "pending"
                                      ? "warning"
                                      : "default"
                              }
                              variant="outlined"
                            />
                          ))}
                        {permit.availableInspections.length > 3 && (
                          <Chip
                            label={`+${permit.availableInspections.length - 3} more`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>

                      <LinearProgress
                        variant="determinate"
                        value={
                          (permit.availableInspections.filter(
                            (i) => i.status === "passed"
                          ).length /
                            permit.availableInspections.length) *
                          100
                        }
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {
                          permit.availableInspections.filter(
                            (i) => i.status === "passed"
                          ).length
                        }{" "}
                        of {permit.availableInspections.length} inspections
                        passed
                      </Typography>
                    </Box>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      No inspections defined yet
                    </Typography>
                  )}

                  {/* Action Buttons */}
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Visibility />}
                      onClick={() =>
                        navigate(
                          `/pp/contractor/permits/${permit.id}/inspections`
                        )
                      }
                    >
                      View Details
                    </Button>

                    {canRequestInspections(permit) && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => {
                          setSelectedPermit(permit);
                          setInspectionDialogOpen(true);
                        }}
                      >
                        Request Inspection
                      </Button>
                    )}

                    {permit.status === "corrections_needed" && (
                      <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        startIcon={<CloudUpload />}
                        onClick={() => navigate("/pp/contractor/uploads")}
                      >
                        Upload Corrections
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      {/* Request Inspection Dialog */}
      <Dialog
        open={inspectionDialogOpen}
        onClose={() => setInspectionDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Request Inspections - {selectedPermit?.number}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Select one or more compatible inspections to request. Some
            inspections cannot be called until others are completed.
          </Alert>

          {selectedInspections.length > 0 && (
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>
                  Selected Inspections ({selectedInspections.length}):
                </strong>{" "}
                {selectedPermit?.availableInspections
                  ?.filter((insp) => selectedInspections.includes(insp.id))
                  .map((insp) => insp.name)
                  .join(", ")}
              </Typography>
            </Alert>
          )}

          {selectedPermit && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Available Inspections
              </Typography>

              <List>
                {getAvailableInspections(selectedPermit).map((inspection) => (
                  <ListItem key={inspection.id}>
                    <ListItemIcon>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedInspections.includes(
                              inspection.id
                            )}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedInspections((prev) => [
                                  ...prev,
                                  inspection.id,
                                ]);
                              } else {
                                setSelectedInspections((prev) =>
                                  prev.filter((id) => id !== inspection.id)
                                );
                              }
                            }}
                          />
                        }
                        label=""
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {inspection.name}
                          {inspection.required && (
                            <Chip
                              label="Required"
                              color="error"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        inspection.dependencies.length > 0
                          ? `Depends on: ${inspection.dependencies.join(", ")}`
                          : "No dependencies"
                      }
                    />
                    <ListItemSecondaryAction>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                          gap: 0.5,
                        }}
                      >
                        {getInspectionStatusChip(inspection.status)}
                        {inspection.required && (
                          <Chip
                            label="Required"
                            color="error"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>

              {selectedInspections.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Upload Photos/Documents
                  </Typography>

                  <Paper
                    sx={{
                      border: 2,
                      borderStyle: "dashed",
                      borderColor: "grey.300",
                      p: 3,
                      textAlign: "center",
                      mb: 2,
                      cursor: "pointer",
                      "&:hover": {
                        borderColor: "primary.main",
                        bgcolor: "action.hover",
                      },
                    }}
                    onClick={() =>
                      document.getElementById("inspection-file-upload").click()
                    }
                  >
                    <CloudUpload
                      sx={{ fontSize: 48, color: "grey.500", mb: 1 }}
                    />
                    <Typography variant="body1">
                      Upload photos and documents
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      JPG, PNG, PDF files (max 5MB each)
                    </Typography>
                  </Paper>

                  <input
                    id="inspection-file-upload"
                    type="file"
                    multiple
                    accept="image/*,.pdf"
                    style={{ display: "none" }}
                    onChange={handleFileUpload}
                  />

                  {uploadedFiles.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Uploaded Files ({uploadedFiles.length})
                      </Typography>
                      <List dense>
                        {uploadedFiles.map((file) => (
                          <ListItem key={file.id}>
                            <ListItemIcon>
                              <PhotoCamera fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={file.name}
                              secondary={file.size}
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveFile(file.id)}
                              >
                                <Delete />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Comments (Optional)"
                    value={inspectionComments}
                    onChange={(e) => setInspectionComments(e.target.value)}
                    placeholder="Add any comments or notes for the inspector..."
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInspectionDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRequestInspections}
            disabled={selectedInspections.length === 0 || submitting}
            startIcon={<Send />}
          >
            {submitting
              ? "Submitting..."
              : `Request ${selectedInspections.length} Inspection(s)`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContractorPermitsPage;
